/* Quality Hangers Wholesale — theme scripts
   One Shopify unit = one case (carton): variant prices are case prices and cart
   quantities are cases, matching the warehouse's carton-based stock. The theme
   shows the price per set (case price / sets per case) and adds a "Case pack"
   note to each line so orders, packing slips and invoices show what's inside. */
(function () {
  'use strict';

  const theme = window.theme || {};

  /* ---------- Helpers ---------- */
  function formatMoney(cents, format) {
    format = format || theme.moneyFormat || '${{amount}}';
    const value = Number(cents) / 100;
    const fmt = (n, decimals, thousands, decimal) => {
      const [int, frac] = n.toFixed(decimals).split('.');
      const withSep = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? withSep + decimal + frac : withSep;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(value, 0, ',', '.');
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
        default: return fmt(value, 2, ',', '.');
      }
    });
  }
  const trimZeros = (s) => s.replace(/([.,])00(?!\d)/, '');

  async function postJSON(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
    return data;
  }

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  async function refreshCartCount() {
    try {
      const cart = await fetch(theme.routes.cartJs).then((r) => r.json());
      updateCartCount(cart.item_count);
    } catch (e) { /* ignore */ }
  }

  let toastTimer;
  function showToast(message) {
    const toast = document.getElementById('CartToast');
    if (!toast) return;
    toast.querySelector('[data-toast-message]').textContent = message;
    toast.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { toast.hidden = true; }, 5000);
  }

  /* ---------- Announcement bar ---------- */
  document.querySelectorAll('[data-announcement]').forEach((track) => {
    const items = track.querySelectorAll('.announcement__item');
    if (items.length < 2) return;
    let i = 0;
    setInterval(() => {
      items[i].classList.remove('is-active');
      i = (i + 1) % items.length;
      items[i].classList.add('is-active');
    }, 4500);
  });

  /* ---------- Mobile menu ---------- */
  const drawer = document.querySelector('[data-menu-drawer]');
  const openBtn = document.querySelector('[data-menu-open]');
  function setDrawer(open) {
    if (!drawer) return;
    drawer.hidden = !open;
    document.body.classList.toggle('drawer-open', open);
    if (openBtn) openBtn.setAttribute('aria-expanded', String(open));
    if (open) drawer.querySelector('input, a, button')?.focus();
    else openBtn?.focus();
  }
  openBtn?.addEventListener('click', () => setDrawer(true));
  document.querySelectorAll('[data-menu-close]').forEach((el) => el.addEventListener('click', () => setDrawer(false)));
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && drawer && !drawer.hidden) setDrawer(false); });

  /* ---------- Sticky header ----------
     Compact once scrolled (desktop). On phones it slides away while scrolling down and comes back on the
     way up, but stays while something in it has focus (search) or the menu is open. */
  const headerSection = document.querySelector('.header--sticky')?.closest('.shopify-section');
  if (headerSection) {
    const phone = window.matchMedia('(max-width: 989px)');
    let lastY = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      ticking = false;
      const y = window.scrollY;
      headerSection.classList.toggle('is-scrolled', y > 40);
      if (Math.abs(y - lastY) < 8) return;
      const keep = !phone.matches || y < lastY || y < 200
        || headerSection.contains(document.activeElement) || document.body.classList.contains('drawer-open');
      headerSection.classList.toggle('is-hidden', !keep);
      lastY = y;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; requestAnimationFrame(onScroll); }
    }, { passive: true });
    onScroll();
    // Other sticky bars sit right below the header: quick order tools, product info, cart summary.
    const setHeight = () => document.documentElement.style.setProperty('--header-h', `${headerSection.offsetHeight}px`);
    new ResizeObserver(setHeight).observe(headerSection);
    setHeight();
  }

  /* ---------- Sliders ---------- */
  document.querySelectorAll('[data-slider]').forEach((slider) => {
    const track = slider.querySelector('[data-slider-track]');
    const prev = slider.querySelector('[data-slider-prev]');
    const next = slider.querySelector('[data-slider-next]');
    const step = () => (track.firstElementChild ? track.firstElementChild.getBoundingClientRect().width + 16 : 300);
    const update = () => {
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    };
    prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  });

  /* ---------- Tabs ---------- */
  document.querySelectorAll('[data-tabs]').forEach((tabs) => {
    const buttons = tabs.querySelectorAll('[role="tab"]');
    buttons.forEach((btn) => btn.addEventListener('click', () => {
      buttons.forEach((b) => {
        const active = b === btn;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-selected', String(active));
        document.getElementById(b.getAttribute('aria-controls')).hidden = !active;
      });
    }));
  });

  /* ---------- Product gallery ---------- */
  function initGallery(root) {
    const slides = [...root.querySelectorAll('.gallery__slide')];
    const thumbs = [...root.querySelectorAll('[data-thumb]')];
    const show = (id) => {
      slides.forEach((s) => s.classList.toggle('is-active', s.dataset.mediaId === String(id)));
      thumbs.forEach((t) => t.classList.toggle('is-active', t.dataset.thumb === String(id)));
    };
    const current = () => slides.findIndex((s) => s.classList.contains('is-active'));
    const go = (dir) => {
      const i = (current() + dir + slides.length) % slides.length;
      show(slides[i].dataset.mediaId);
    };
    thumbs.forEach((t) => t.addEventListener('click', () => show(t.dataset.thumb)));
    root.querySelector('[data-gallery-prev]')?.addEventListener('click', () => go(-1));
    root.querySelector('[data-gallery-next]')?.addEventListener('click', () => go(1));
    return { show };
  }

  /* ---------- Product form (case selector) ---------- */
  document.querySelectorAll('[data-product-section]').forEach((section) => {
    const dataEl = section.querySelector('[data-product-json]');
    const form = section.querySelector('[data-product-form]');
    if (!dataEl || !form) return;
    const data = JSON.parse(dataEl.textContent);
    const gallery = section.querySelector('[data-gallery]') ? initGallery(section.querySelector('[data-gallery]')) : null;

    const $ = (sel) => section.querySelector(sel);
    const $$ = (sel) => section.querySelectorAll(sel);
    const idInput = form.querySelector('[data-variant-id]');
    const qtyInput = form.querySelector('[data-quantity]');
    const caseSelect = form.querySelector('[data-case-select]');
    const addBtn = form.querySelector('[data-add-button]');
    const addLabel = form.querySelector('[data-add-label]');
    const errorEl = form.querySelector('[data-form-error]');
    const casePackInput = form.querySelector('[data-case-pack]');
    const setPrice = (v) => (v.sets_per_case > 1 ? Math.round(v.price / v.sets_per_case) : v.price);
    // "pack" (retail pack), "box" (inner box), "set" or "case" (bulk); a single-set variant (GRP84 grips) is its own unit
    const unitOf = (v) => (v.sets_per_case > 1 ? v.unit || 'set' : v.sale || 'case');
    const plural = (u) => (u === 'box' ? 'boxes' : `${u}s`);
    // what is counted: hangers, or grips for the GRP84 accessory (snippets/pack-values)
    const itemsOf = (v) => plural(v.item || 'hanger');
    const stepOf = (v) => v.step || 1;
    const count = (n, word) => `${n} ${n === 1 ? word : plural(word)}`;

    let variant = data.variants.find((v) => String(v.id) === idInput.value) || data.variants[0];

    const selectedOptions = () =>
      [...form.querySelectorAll('.option')].map((fs) => fs.querySelector('input:checked')?.value);

    function caseLabel(i, v) {
      const spc = v.sets_per_case, hps = v.hangers_per_set;
      const total = trimZeros(formatMoney(v.price * i));
      if (spc > 1) {
        const sets = spc * i;
        return `${count(i, 'case')} – ${sets} ${plural(unitOf(v))}${hps ? ` (${sets * hps} ${itemsOf(v)})` : ''} – ${total}`;
      }
      return `${count(i, v.sale || 'case')}${hps ? ` (${hps * i} ${itemsOf(v)})` : ''} – ${total}`;
    }

    let lastStep = stepOf(variant);
    function renderCaseSelect() {
      const step = stepOf(variant);
      // keep the chosen quantity across colors, but not between sets-in-5s and cartons
      const selected = step === lastStep ? Number(caseSelect.value) || step : step;
      lastStep = step;
      caseSelect.innerHTML = '';
      const maxQty = data.maxCases * step;
      const max = variant.max_cases ? Math.min(maxQty, variant.max_cases) : maxQty;
      for (let i = step; i <= Math.max(max, step); i += step) {
        const opt = new Option(caseLabel(i, variant), i);
        if (i === selected) opt.selected = true;
        caseSelect.add(opt);
      }
    }

    function updateTotals() {
      const qty = Number(caseSelect.value) || stepOf(variant);
      const spc = variant.sets_per_case;
      qtyInput.value = qty;
      const totalEl = $('[data-case-total]');
      if (totalEl) totalEl.textContent = formatMoney(variant.price * qty);
      const detail = $('[data-case-total-detail]');
      if (detail) detail.textContent = spc > 1 ? `${spc * qty} ${plural(unitOf(variant))} × ${formatMoney(setPrice(variant))}` : (qty > 1 ? `${count(qty, unitOf(variant))} × ${formatMoney(variant.price)}` : '');
    }

    function renderVariant() {
      const spc = variant.sets_per_case, hps = variant.hangers_per_set;
      const u = unitOf(variant);
      const us = u === 'pack' ? 'retail packs' : plural(u);
      const items = itemsOf(variant);
      const step = stepOf(variant);
      idInput.value = variant.id;

      // Price
      const amount = $('[data-price-amount]');
      if (amount) amount.textContent = formatMoney(setPrice(variant));
      const unit = $('[data-price-unit]');
      if (unit) unit.textContent = `/ ${u}`;
      const retail = $('[data-retail-badge]');
      if (retail) retail.hidden = u !== 'pack';
      const compare = $('[data-price-compare]');
      const saving = $('[data-price-saving]');
      const onSale = variant.compare_at_price > variant.price;
      const unitCompare = spc > 1 ? Math.round(variant.compare_at_price / spc) : variant.compare_at_price;
      if (compare) { compare.hidden = !onSale; compare.textContent = onSale ? formatMoney(unitCompare) : ''; }
      if (saving) {
        saving.hidden = !onSale;
        saving.textContent = onSale ? `Save ${Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price)}%` : '';
      }
      const perHanger = $('[data-price-per-hanger]');
      if (perHanger) {
        perHanger.hidden = !hps;
        // grips cost a fraction of a cent each, so they're quoted per 100 (snippets/price)
        if (hps) perHanger.textContent = variant.item && variant.item !== 'hanger'
          ? `${formatMoney(Math.round(variant.price * 100 / (hps * spc)))} per 100 ${items}`
          : `${formatMoney(Math.round(variant.price / (hps * spc)))} per hanger`;
      }
      if (casePackInput) {
        casePackInput.value = variant.case_pack || '';
        casePackInput.disabled = !variant.case_pack;
      }

      // Pack info
      const box = $('[data-pack-box]');
      if (box) box.hidden = !hps && spc <= 1;
      const set = (sel, val) => { const el = $(sel); if (el) el.textContent = val; };
      set('[data-pack-per-set]', hps);
      set('[data-pack-per-set-label]', `${items} per ${u}`);
      set('[data-pack-sets]', spc);
      set('[data-pack-sets-label]', `${us} per case`);
      set('[data-pack-total]', spc * hps);
      set('[data-pack-total-label]', `${items} per case`);
      const setsWrap = $('[data-pack-sets-wrap]'); if (setsWrap) setsWrap.hidden = spc <= 1;
      const totalWrap = $('[data-pack-total-wrap]'); if (totalWrap) totalWrap.hidden = spc <= 1 || !hps;
      set('[data-case-hint]', spc > 1 ? `Case of ${spc} ${us}` : step > 1 ? `Sold in ${step}s` : `Sold by the ${u}`);
      const note = $('[data-pack-note]');
      if (note) {
        note.hidden = spc <= 1 || !hps;
        const total = spc * hps;
        set('[data-note-text]', u === 'pack'
          ? `Each retail pack holds ${hps} hangers with a color card, ${spc} packs per case – ${total} hangers in total.`
          : u === 'box'
            ? `Each inner box holds ${hps} hangers, ${spc} boxes per case – ${total} hangers in total.`
            : `Each set includes ${hps} ${items}, ${spc} sets per case – ${total} ${items} in total.`);
      }
      const specPack = $('[data-spec-pack]');
      if (specPack) {
        specPack.hidden = !hps;
        set('[data-spec-pack-value]', spc > 1
          ? `${hps} ${items} per ${u}, ${spc} ${us} per case (${spc * hps} ${items})`
          : u !== 'case' ? `${hps} ${items} per ${u}` : `${hps} ${items} per case (bulk)`);
      }
      set('[data-spec-min]', step > 1 ? `${step} ${plural(u)}` : `1 ${variant.sale || 'case'}`);

      // SKU + option labels
      $$('[data-sku]').forEach((el) => { el.hidden = !variant.sku; });
      $$('[data-sku-value]').forEach((el) => { el.textContent = variant.sku || ''; });
      variant.options.forEach((val, i) => {
        const fs = form.querySelector(`.option[data-option-index="${i}"] [data-option-selected]`);
        if (fs) fs.textContent = val;
        const spec = section.querySelector(`[data-spec-option="${i}"]`);
        if (spec) spec.textContent = val;
      });

      // Availability
      addBtn.disabled = !variant.available;
      addLabel.textContent = variant.available ? 'Add to cart' : 'Sold out';
      // Stock line: exact count up to 10, then "10+" (same rule as Quick Order)
      const stockLine = $('[data-stock-line]');
      if (stockLine) {
        const n = variant.available ? variant.max_cases : null;
        stockLine.hidden = !n;
        if (n) stockLine.querySelector('[data-stock-count]').textContent = n > 10 ? '10+' : n;
      }
      // "Notify me when back in stock" for sold-out variants (dialog: snippets/back-in-stock)
      const bisBtn = $('[data-bis-open]');
      if (bisBtn) {
        bisBtn.hidden = variant.available;
        bisBtn.dataset.variant = variant.id;
        bisBtn.dataset.sku = variant.sku || '';
      }

      if (variant.featured_media_id && gallery) gallery.show(variant.featured_media_id);
      markUnavailable();
      renderCaseSelect();
      updateTotals();

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    // Dim option values that don't exist together with the other current choices
    // (e.g. Silver only comes in the adult size).
    function markUnavailable() {
      const opts = variant.options;
      form.querySelectorAll('.option').forEach((fs) => {
        const idx = Number(fs.dataset.optionIndex);
        fs.querySelectorAll('input').forEach((input) => {
          const exists = data.variants.some((v) => v.options[idx] === input.value &&
            v.options.every((o, i) => i === idx || o === opts[i]));
          const label = fs.querySelector(`label[for="${input.id}"]`);
          if (label) label.classList.toggle('is-unavailable', !exists);
        });
      });
    }

    // Exact match, or else the closest option that has the value just picked
    // (keeps as many other choices as possible, in stock first).
    // Color clicks (preferStock) skip a sold-out exact match when that color has a packing in stock.
    // wooden listings (ph, snippets/best-pack): a color lands on its in-stock packing with the lowest price per
    // hanger, unless a packing was picked by hand
    let packPicked = false;
    function bestMatch(opts, changedIdx, preferStock = false) {
      if (preferStock && !packPicked) {
        const cheapest = data.variants
          .filter((v) => v.available && v.ph != null && v.options[changedIdx] === opts[changedIdx])
          .sort((a, b) => a.ph - b.ph)[0];
        if (cheapest) return cheapest;
      }
      const exact = data.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      if (exact && (exact.available || !preferStock)) return exact;
      if (preferStock) {
        const inStock = data.variants.filter((v) => v.available && v.options[changedIdx] === opts[changedIdx]);
        const keep = (v) => v.options.filter((o, i) => o === opts[i]).length;
        if (inStock.length) return inStock.sort((a, b) => keep(b) - keep(a))[0];
        if (exact) return exact;
      }
      const score = (v) => v.options.filter((o, i) => o === opts[i]).length + (v.available ? 0.5 : 0);
      return data.variants
        .filter((v) => v.options[changedIdx] === opts[changedIdx])
        .sort((a, b) => score(b) - score(a))[0];
    }

    form.addEventListener('change', (e) => {
      if (e.target.matches('.option input')) {
        const changedIdx = Number(e.target.closest('.option').dataset.optionIndex);
        const isColor = e.target.closest('.option').classList.contains('option--swatches');
        if (!isColor) packPicked = true;
        const match = bestMatch(selectedOptions(), changedIdx, isColor);
        if (match) {
          variant = match;
          // reflect the auto-picked choices in the radios
          form.querySelectorAll('.option').forEach((fs) => {
            const val = variant.options[Number(fs.dataset.optionIndex)];
            fs.querySelectorAll('input').forEach((input) => { input.checked = input.value === val; });
          });
          renderVariant();
        } else {
          addBtn.disabled = true;
          addLabel.textContent = 'Unavailable';
        }
      }
      if (e.target === caseSelect) updateTotals();
    });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      errorEl.hidden = true;
      addBtn.classList.add('is-loading');
      try {
        const qty = Number(qtyInput.value);
        const item = { id: variant.id, quantity: qty };
        if (casePackInput && !casePackInput.disabled) item.properties = { 'Case pack': casePackInput.value };
        await postJSON(theme.routes.cartAdd, { items: [item] });
        await refreshCartCount();
        showToast(`${count(qty, unitOf(variant))} added to your cart`);
      } catch (err) {
        errorEl.textContent = err.message;
        errorEl.hidden = false;
      } finally {
        addBtn.classList.remove('is-loading');
      }
    });

    markUnavailable();
    renderCaseSelect();
    updateTotals();
  });

  /* ---------- Family comparison table: keep the "Case pack" note in step with the chosen option ---------- */
  document.addEventListener('change', (e) => {
    const select = e.target.closest('[data-compare-variant]');
    if (!select) return;
    const casePack = select.form && select.form.elements['properties[Case pack]'];
    const opt = select.options[select.selectedIndex];
    if (casePack && opt) casePack.value = opt.dataset.case || '';
  });

  /* ---------- Quick add on product cards ---------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-quick-add]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('button');
    btn.classList.add('is-loading');
    try {
      const item = { id: Number(form.elements.id.value), quantity: Number(form.elements.quantity.value) };
      const casePack = form.elements['properties[Case pack]'];
      if (casePack) item.properties = { 'Case pack': casePack.value };
      await postJSON(theme.routes.cartAdd, { items: [item] });
      await refreshCartCount();
      showToast('1 case added to your cart');
    } catch (err) {
      showToast(err.message);
    } finally {
      btn.classList.remove('is-loading');
    }
  });

  /* ---------- Product recommendations ---------- */
  document.querySelectorAll('[data-recommendations]').forEach(async (el) => {
    if (el.children.length && el.querySelector('.product-grid')) return;
    try {
      const html = await fetch(el.dataset.url).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-recommendations]');
      if (fresh && fresh.innerHTML.trim()) el.innerHTML = fresh.innerHTML;
    } catch (e) { /* ignore */ }
  });

  /* ---------- Cart page: +/- one case ---------- */
  const cartSection = document.querySelector('[data-cart-section]');
  if (cartSection) {
    const setLine = async (key, quantity) => {
      cartSection.classList.add('is-updating');
      try {
        await postJSON(theme.routes.cartChange, { id: key, quantity });
      } catch (err) {
        alert(err.message);
      }
      window.location.reload();
    };

    cartSection.addEventListener('click', (e) => {
      const item = e.target.closest('.cart-item');
      if (!item) return;
      const qty = Number(item.dataset.quantity);
      const step = Number(item.dataset.step) || 1; // sets of grips go in 5s
      if (e.target.closest('[data-qty-plus]')) setLine(item.dataset.key, qty + step);
      else if (e.target.closest('[data-qty-minus]')) setLine(item.dataset.key, Math.max(qty - step, 0));
    });
  }

  /* ---------- "Add non-slip grips" offer (snippets/grips-offer) ----------
     Adds the GRP84 set variant in its step (5 sets); the cart page reloads to show it. */
  document.querySelectorAll('[data-grips-add]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      btn.classList.add('is-loading');
      try {
        const item = { id: Number(btn.dataset.variant), quantity: Number(btn.dataset.quantity) || 1 };
        if (btn.dataset.casePack) item.properties = { 'Case pack': btn.dataset.casePack };
        await postJSON(theme.routes.cartAdd, { items: [item] });
        if ('reload' in btn.dataset) { window.location.reload(); return; }
        await refreshCartCount();
        showToast(`${item.quantity} sets of grips added to your cart`);
        btn.textContent = '✓ Added to cart';
        btn.disabled = true;
      } catch (err) {
        alert(err.message);
      } finally {
        btn.classList.remove('is-loading');
      }
    });
  });

  /* ---------- Cart / quick order: download the cart as PDF ----------
     jsPDF + AutoTable load from cdnjs on the first click. The cart data (data-cart-pdf) is rendered by
     sections/main-cart: read from the page on the cart page (it reloads after every change), fetched
     fresh from the cart page elsewhere (the quick order page saves without reloading).
     The builder is shared as theme.pdf (staff quote page: assets/staff-quote.js). Optional data keys:
     title, meta (extra header lines), customer {name, company, email, phone}, totalLabel, noteTitle,
     disclaimer, filename, noSave (build and return the jsPDF doc without downloading it). */
  {
    const loadScript = (src) => new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src;
      s.onload = resolve;
      s.onerror = () => reject(new Error('Could not load the PDF tools. Please check your connection and try again.'));
      document.head.appendChild(s);
    });
    // Through a canvas, so any format the browser shows (webp included) ends up as JPEG/PNG for jsPDF.
    const loadImage = (url, type = 'image/jpeg') => new Promise((resolve) => {
      if (!url) { resolve(null); return; }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const c = document.createElement('canvas');
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        const ctx = c.getContext('2d');
        if (type === 'image/jpeg') { ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, c.width, c.height); }
        ctx.drawImage(img, 0, 0);
        try { resolve({ data: c.toDataURL(type, 0.85), w: c.width, h: c.height }); } catch (e) { resolve(null); }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });

    const buildPdf = async (data) => {
      const [logo, ...thumbs] = await Promise.all([loadImage(data.logo, 'image/png'), ...data.items.map((i) => loadImage(i.image))]);
      const doc = new window.jspdf.jsPDF({ unit: 'pt', format: 'letter' });
      const W = doc.internal.pageSize.getWidth();
      const H = doc.internal.pageSize.getHeight();
      const M = 40;
      const ink = [20, 20, 20];
      const muted = [115, 115, 115];
      const gold = [242, 195, 65];
      const line = [225, 222, 214];

      // Header: logo left, title + date + contact right
      let top = M;
      let logoH = 0;
      if (logo) {
        logoH = 58;
        doc.addImage(logo.data, 'PNG', M, top, (logo.w / logo.h) * logoH, logoH);
      }
      doc.setTextColor(...ink).setFont('helvetica', 'bold').setFontSize(20);
      doc.text(data.title || 'Cart Summary', W - M, top + 4, { align: 'right', baseline: 'top' });
      doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(...muted);
      const contact = [...(data.meta || []), data.date, data.domain, data.email, data.phone].filter(Boolean);
      contact.forEach((t, n) => doc.text(t, W - M, top + 32 + n * 12, { align: 'right', baseline: 'top' }));
      top = Math.max(top + logoH, top + 32 + contact.length * 12) + 14;
      doc.setDrawColor(...gold).setLineWidth(2).line(M, top, W - M, top);
      top += 16;

      // Quotes: who it is for
      const c = data.customer || {};
      const forLines = [c.name, c.company, c.email, c.phone].filter(Boolean);
      if (forLines.length) {
        doc.setFont('helvetica', 'bold').setFontSize(8).setTextColor(...muted).text('PREPARED FOR', M, top, { baseline: 'top' });
        doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(...ink);
        forLines.forEach((t, n) => doc.text(t, M, top + 13 + n * 13, { baseline: 'top' }));
        top += 13 + forLines.length * 13 + 12;
      }

      // Items
      const thumbSize = 40;
      doc.autoTable({
        startY: top,
        margin: { left: M, right: M, bottom: 50 },
        head: [['', 'SKU / Hanger', 'Per case', 'Cases', 'Price / case', 'Price / set', 'Per hanger', 'Total']],
        body: data.items.map((i) => [
          '',
          // first line is left blank: the bold SKU (and badge) is drawn there in didDrawCell
          [' ', i.title, [i.variant, /hangers/.test(i.variant) ? '' : i.pack].filter(Boolean).join(' · ')].filter(Boolean).join('\n'),
          i.perCase ? `${i.perCase} ${i.items || 'hangers'}` : '–',
          i.sale && i.sale !== 'case' ? `${i.cases} ${i.sale}s` : String(i.cases),
          i.price,
          i.perSet ? [i.perSet, i.setLabel].filter(Boolean).join('\n') : '–',
          i.perHanger || '–',
          i.total
        ]),
        theme: 'plain',
        // keep each item row whole: a row split over a page break had no item behind its second half
        // (didDrawCell crashed with "reading 'sku'" on a 13-line staff quote, 2026-10-19)
        rowPageBreak: 'avoid',
        styles: { font: 'helvetica', fontSize: 8.5, textColor: ink, cellPadding: { top: 7, bottom: 7, left: 5, right: 5 }, valign: 'middle', lineColor: line },
        headStyles: { fontStyle: 'bold', fontSize: 7.5, textColor: muted, fillColor: [248, 245, 238], valign: 'middle' },
        columnStyles: {
          0: { cellWidth: thumbSize + 10, minCellHeight: thumbSize + 14 },
          1: { cellWidth: 'auto', valign: 'top' },
          2: { cellWidth: 58, halign: 'center' },
          3: { cellWidth: 34, halign: 'center', fontStyle: 'bold', fontSize: 10 },
          4: { cellWidth: 56, halign: 'right' },
          5: { cellWidth: 50, halign: 'right' },
          6: { cellWidth: 48, halign: 'right', textColor: muted },
          7: { cellWidth: 60, halign: 'right', fontStyle: 'bold' }
        },
        didParseCell: (h) => {
          if (h.section === 'head' && h.column.index >= 4) h.cell.styles.halign = 'right';
          if (h.section === 'head' && (h.column.index === 2 || h.column.index === 3)) h.cell.styles.halign = 'center';
          if (h.section === 'body') {
            h.cell.styles.lineWidth = { bottom: 0.5 };
            if (h.column.index === 1) h.cell.styles.cellPadding = { top: 9, bottom: 7, left: 5, right: 5 };
          }
        },
        didDrawCell: (h) => {
          if (h.section !== 'body') return;
          const item = data.items[h.row.index];
          if (!item) return;
          const { x, y, height } = h.cell;
          if (h.column.index === 0) {
            const t = thumbs[h.row.index];
            if (t) {
              const s = Math.min(thumbSize / t.w, thumbSize / t.h);
              const w = t.w * s;
              const hh = t.h * s;
              doc.addImage(t.data, 'JPEG', x + 5 + (thumbSize - w) / 2, y + (height - hh) / 2, w, hh);
            }
          }
          if (h.column.index === 1) {
            const tx = x + 5;
            const ty = y + 8;
            doc.setFont('helvetica', 'bold').setFontSize(10.5).setTextColor(...ink);
            doc.text(item.sku, tx, ty, { baseline: 'top' });
            if (item.retail) {
              const bx = tx + doc.getTextWidth(item.sku) + 6;
              doc.setFontSize(6.5);
              const label = 'RETAIL PACKED';
              const bw = doc.getTextWidth(label) + 10;
              doc.setFillColor(...gold).roundedRect(bx, ty - 0.5, bw, 11, 5.5, 5.5, 'F');
              doc.setTextColor(...ink).text(label, bx + 5, ty + 2.2, { baseline: 'top' });
            }
          }
        }
      });

      // Totals
      let y = doc.lastAutoTable.finalY + 18;
      const boxW = 230;
      const rows = [
        ['Cases', String(data.cases)],
        ['Hangers', data.hangers.toLocaleString('en-US')],
        ...(data.sets ? [['Sets of grips', String(data.sets)]] : []),
        ...(data.discounts || []).map((d) => ['Discount', d]),
        ['Shipping', 'Free within the U.S.']
      ];
      if (y + 30 + rows.length * 16 > H - 60) { doc.addPage(); y = M; }
      doc.setFontSize(9.5);
      rows.forEach(([k, v]) => {
        doc.setFont('helvetica', 'normal').setTextColor(...muted).text(k, W - M - boxW, y);
        doc.setTextColor(...ink).text(v, W - M, y, { align: 'right' });
        y += 16;
      });
      doc.setDrawColor(...line).setLineWidth(0.75).line(W - M - boxW, y - 6, W - M, y - 6);
      y += 10;
      doc.setFont('helvetica', 'bold').setFontSize(13).setTextColor(...ink);
      doc.text(data.totalLabel || 'Subtotal', W - M - boxW, y);
      doc.text(data.subtotal, W - M, y, { align: 'right' });

      // Notes (left of the totals)
      let ny = doc.lastAutoTable.finalY + 18;
      const noteW = W - 2 * M - boxW - 30;
      doc.setFont('helvetica', 'normal').setFontSize(8.5).setTextColor(...muted);
      if (data.note) {
        doc.setFont('helvetica', 'bold').setTextColor(...ink).text(data.noteTitle || 'Order notes', M, ny);
        ny += 12;
        doc.setFont('helvetica', 'normal').setTextColor(...muted);
        const noteLines = doc.splitTextToSize(data.note, noteW);
        doc.text(noteLines, M, ny);
        ny += noteLines.length * 11 + 8;
      }
      doc.text(doc.splitTextToSize(data.disclaimer || 'Prices in USD. Taxes are calculated at checkout. Stock is reserved only when the order is placed. Orders placed by 1:30 PM ET ship the same day.', noteW), M, ny);

      // Footer on every page
      const pages = doc.getNumberOfPages();
      for (let p = 1; p <= pages; p += 1) {
        doc.setPage(p);
        doc.setDrawColor(...line).setLineWidth(0.5).line(M, H - 34, W - M, H - 34);
        doc.setFont('helvetica', 'normal').setFontSize(8).setTextColor(...muted);
        doc.text([data.shop, data.domain, data.email].filter(Boolean).join('  ·  '), M, H - 22);
        doc.text(`Page ${p} of ${pages}`, W - M, H - 22, { align: 'right' });
      }

      const stamp = new Date().toISOString().slice(0, 10);
      if (!data.noSave) doc.save(data.filename || `${data.shop.replace(/[^\w]+/g, '-')}-cart-${stamp}.pdf`);
      return doc;
    };

    const ensurePdfLibs = async () => {
      if (!window.jspdf) await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
      if (!window.jspdf.jsPDF.API.autoTable) await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js');
    };
    theme.pdf = { build: async (data) => { await ensurePdfLibs(); return buildPdf(data); } };
    window.theme = theme;

    const pdfBtn = document.querySelector('[data-cart-pdf-download]');
    if (pdfBtn) pdfBtn.addEventListener('click', async () => {
      pdfBtn.classList.add('is-loading');
      try {
        await ensurePdfLibs();
        let source = document.querySelector('[data-cart-pdf]');
        if (!source) {
          await document.querySelector('[data-quick-order]')?.flush?.(); // let typed quantities reach the cart first
          const html = await fetch(theme.routes.cart).then((r) => r.text());
          source = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-pdf]');
        }
        if (!source) throw new Error('Your cart is empty. Add some cases first.');
        await buildPdf(JSON.parse(source.textContent));
      } catch (err) {
        alert(err.message);
      } finally {
        pdfBtn.classList.remove('is-loading');
      }
    });
  }

  /* ---------- Quick order page ----------
     Every row mirrors the cart: a number of cases is saved after a short pause (or when the field
     is left), 0 removes it. Saves run one at a time. A variant split over several cart lines
     (lines with properties) is collapsed onto its first line so the row and the cart always agree. */
  const qo = document.querySelector('[data-quick-order]');
  if (qo) {
    const inputs = [...qo.querySelectorAll('[data-qo-qty]')];
    const moneyFormat = JSON.parse(document.querySelector('[data-qo-money]')?.textContent || '"${{amount}}"');
    const fmt = (cents) => moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    const errorBox = qo.querySelector('[data-qo-error]');
    // write = false while totals update during typing, so "1" on the way to "10" isn't rounded to 5 mid-keystroke
    const clamp = (input, write = true) => {
      const step = Number(input.dataset.step) || 1; // sets of grips: multiples of 5, rounded up
      let n = Math.max(0, Math.floor(Number(input.value) || 0));
      n = Math.ceil(n / step) * step;
      if (input.dataset.max && n > Number(input.dataset.max)) n = Math.floor(Number(input.dataset.max) / step) * step;
      if (write && String(n) !== input.value) input.value = n;
      // the variant panel shows "Add 1 case" at 0 and the − n + stepper above it
      input.closest('[data-qo-variant]')?.classList.toggle('has-qty', n > 0);
      return n;
    };
    /* Swatch / pill picker over the row's hidden dropdown. Each <option> carries its option values in data-opts
       ("Pink||4 boxes of 50 · 200 hangers"); a button is option index + value. */
    const optsOf = (o) => (o.dataset.opts || '').split('||');
    // Swatches (Colors cell, [data-qo-pick]) and compact dropdowns (Case pack cell, [data-qo-pick-select]):
    // mark the current choice, cross out combinations that don't exist or are sold out, badge colors in the cart.
    const refreshPicker = (row) => {
      const select = row.querySelector('[data-qo-select]');
      if (!select) return;
      const options = [...select.options];
      const cur = optsOf(select.selectedOptions[0]);
      const counts = qo.counts || new Map();
      const exactFor = (idx, val) => options.find((o) => optsOf(o).every((v, i) => (i === idx ? v === val : v === cur[i])));
      row.querySelectorAll('[data-qo-pick]').forEach((b) => {
        const idx = Number(b.dataset.qoPick);
        const val = b.dataset.value;
        b.classList.toggle('is-selected', cur[idx] === val);
        b.setAttribute('aria-pressed', cur[idx] === val ? 'true' : 'false');
        // a color is crossed out only when none of its packings is in stock (clicking it jumps to one that is)
        b.classList.toggle('is-unavailable', !options.some((o) => optsOf(o)[idx] === val && !('soldout' in o.dataset)));
        const badge = b.querySelector('[data-qo-badge]');
        if (badge) {
          const n = options.filter((o) => optsOf(o)[idx] === val).reduce((s, o) => s + (counts.get(o.value) || 0), 0);
          badge.textContent = n;
          badge.hidden = !n;
        }
      });
      row.querySelectorAll('[data-qo-pick-select]').forEach((s) => {
        const idx = Number(s.dataset.qoPickSelect);
        s.value = cur[idx];
        [...s.options].forEach((o) => {
          const exact = exactFor(idx, o.value);
          o.textContent = o.dataset.short + (!exact ? ' · n/a' : 'soldout' in exact.dataset ? ' · sold out' : '');
        });
      });
      const name = row.querySelector('[data-qo-color-name]');
      const ci = row.querySelector('[data-qo-pick]')?.dataset.qoPick;
      if (name && ci !== undefined) name.textContent = cur[Number(ci)];
    };
    // pick option `idx` = `val`: the variant with the same other choices, else the closest in-stock one
    // preferStock (color swatches): land on a packing we have; a packing/size pick keeps the color when that exists
    const pickOption = (row, idx, val, preferStock = false) => {
      const select = row.querySelector('[data-qo-select]');
      const cur = optsOf(select.selectedOptions[0]);
      const withVal = [...select.options].filter((o) => optsOf(o)[idx] === val);
      const kept = (o) => optsOf(o).filter((v, i) => i !== idx && v === cur[i]).length;
      // wooden listings (data-ph, snippets/best-pack): a color lands on its in-stock packing with the lowest
      // price per hanger, unless a packing was picked by hand in this row
      const byPrice = preferStock && !row.dataset.packPicked && withVal.some((o) => o.dataset.ph);
      const score = (o) => (byPrice ? ('soldout' in o.dataset ? 0 : 1e6) - Number(o.dataset.ph || 1e5)
        : preferStock ? ('soldout' in o.dataset ? 0 : 100) + kept(o) : kept(o) * 2 + ('soldout' in o.dataset ? 0 : 1));
      const pick = withVal.sort((a, c) => score(c) - score(a))[0];
      if (pick && pick.value !== select.value) { select.value = pick.value; select.dispatchEvent(new Event('change')); }
      else refreshPicker(row); // a compact dropdown snaps back if nothing changed
    };
    qo.addEventListener('click', (e) => {
      const b = e.target.closest('[data-qo-pick]');
      if (b) pickOption(b.closest('[data-qo-row]'), Number(b.dataset.qoPick), b.dataset.value, true);
    });
    qo.addEventListener('change', (e) => {
      const s = e.target.closest('[data-qo-pick-select]');
      if (s) {
        const row = s.closest('[data-qo-row]');
        row.dataset.packPicked = '1';
        pickOption(row, Number(s.dataset.qoPickSelect), s.value);
      }
    });

    const update = () => {
      let cases = 0, hangers = 0, total = 0;
      const counts = new Map(); // variant id -> cases, for the dropdown labels
      const sales = new Map();
      inputs.forEach((i) => {
        const n = clamp(i, false);
        counts.set(i.dataset.variant, n);
        sales.set(i.dataset.variant, i.dataset.sale || 'case');
        if ((i.dataset.sale || 'case') === 'case') cases += n; // sets of grips aren't cases
        hangers += n * Number(i.dataset.hangers || 0);
        total += n * Number(i.dataset.price);
      });
      // one row per listing: it's "picked" when any of its variants is in the cart
      qo.querySelectorAll('[data-qo-row]').forEach((r) => {
        r.classList.toggle('is-picked', (r.dataset.variants || '').split(' ').some((v) => counts.get(v) > 0));
      });
      qo.querySelectorAll('[data-qo-select] option').forEach((o) => {
        const n = counts.get(o.value) || 0;
        const unit = `${sales.get(o.value) || 'case'}${n === 1 ? '' : 's'}`;
        o.textContent = o.dataset.label + (n ? ` · ${n} ${unit} in cart` : '') + ('soldout' in o.dataset && !n ? ' · sold out' : '');
      });
      qo.counts = counts;
      qo.querySelectorAll('[data-qo-row]').forEach((r) => refreshPicker(r));
      qo.querySelector('[data-qo-count]').textContent = `${cases} ${cases === 1 ? 'case' : 'cases'}`;
      qo.querySelector('[data-qo-hangers]').textContent = hangers ? `${hangers.toLocaleString('en-US')} hangers` : '';
      qo.querySelector('[data-qo-total]').textContent = fmt(total);
    };

    const setStatus = (input, text, isError = false) => {
      const s = input.closest('.quick-order__qty')?.querySelector('[data-qo-status]');
      if (!s) return;
      s.textContent = text;
      s.classList.toggle('is-error', isError);
    };
    const linesOf = (cart, input) => cart.items.filter((l) => String(l.variant_id) === input.dataset.variant);
    const inCart = (cart, input) => linesOf(cart, input).reduce((n, l) => n + l.quantity, 0);

    let cart = null; // last cart the server returned
    let queue = Promise.resolve();
    const timers = new Map();

    // Sends rows (input -> wanted cases) to the cart in one /cart/update.js call.
    const push = async (wants) => {
      if (!cart) cart = await fetch('/cart.js').then((r) => r.json());
      const updates = {};
      wants.forEach((want, input) => {
        const lines = linesOf(cart, input);
        if (lines.length) lines.forEach((l, n) => { updates[l.key] = n === 0 ? want : 0; });
        else updates[input.dataset.variant] = want;
      });
      cart = await postJSON('/cart/update.js', { updates });
      updateCartCount(cart.item_count);
      wants.forEach((want, input) => {
        const got = inCart(cart, input);
        input.dataset.saved = got;
        if (got === want) {
          setStatus(input, got ? '✓ In cart' : '');
        } else {
          input.value = got;
          setStatus(input, got ? `Only ${got} available · ✓ In cart` : 'Not available right now', true);
        }
      });
      update();
    };

    // Saves the given rows together, queued behind any save in flight. If a batch is refused,
    // the rows are retried one by one so a single problem row doesn't block the rest.
    const save = (...rows) => {
      rows.forEach((i) => { clearTimeout(timers.get(i)); timers.delete(i); });
      queue = queue.then(async () => {
        const wants = new Map();
        rows.forEach((i) => {
          const want = clamp(i); // latest value, even if the buyer kept typing while queued
          if (String(want) === i.dataset.saved) setStatus(i, want ? '✓ In cart' : '');
          else wants.set(i, want);
        });
        if (!wants.size) return;
        const batches = [wants];
        let failed = null;
        while (batches.length) {
          const batch = batches.shift();
          try {
            await push(batch);
          } catch (err) {
            cart = null; // re-read the cart before the next save
            if (batch.size > 1) {
              batch.forEach((want, i) => batches.push(new Map([[i, want]])));
            } else {
              failed = err;
              batch.forEach((want, i) => setStatus(i, 'Not saved, try again', true));
            }
          }
        }
        errorBox.textContent = failed ? failed.message : '';
        errorBox.hidden = !failed;
      });
      return queue;
    };

    inputs.forEach((i) => {
      i.addEventListener('input', () => {
        update();
        setStatus(i, 'Saving…');
        clearTimeout(timers.get(i));
        timers.set(i, setTimeout(() => save(i), 800));
      });
      i.addEventListener('change', () => save(i)); // leaving the field or pressing Enter saves right away
      i.addEventListener('focus', () => i.select());
    });

    // "Add 1 case" saves one step right away; − / + change it by one step (saved after a short pause,
    // so several quick taps become one cart update). At 0 the row goes back to "Add 1 case".
    qo.addEventListener('click', (e) => {
      const add = e.target.closest('[data-qo-add]');
      const stepBtn = e.target.closest('[data-qo-step]');
      if (!add && !stepBtn) return;
      const input = (add || stepBtn).closest('[data-qo-variant]').querySelector('[data-qo-qty]');
      const step = Number(input.dataset.step) || 1;
      const now = Math.max(0, Math.floor(Number(input.value) || 0));
      input.value = add ? Math.max(now, step) : Math.max(0, now + Number(stepBtn.dataset.qoStep) * step);
      clamp(input);
      input.dispatchEvent(new Event('input'));
      if (add) save(input);
    });

    // Dropdown on listings with several variants: show the chosen variant's panel, photo, SKU and link.
    qo.querySelectorAll('[data-qo-select]').forEach((select) => {
      select.addEventListener('change', () => {
        const row = select.closest('[data-qo-row]');
        const opt = select.options[select.selectedIndex];
        row.querySelectorAll('[data-qo-variant]').forEach((p) => { p.hidden = p.dataset.qoVariant !== select.value; });
        const img = row.querySelector('.quick-order__thumb img');
        if (img && opt.dataset.img) { img.removeAttribute('srcset'); img.src = opt.dataset.img; }
        row.querySelector('[data-qo-sku]').textContent = opt.dataset.sku;
        const retailTag = row.querySelector('[data-qo-retail-tag]');
        if (retailTag) retailTag.hidden = !('retail' in opt.dataset);
        refreshPicker(row);
        row.querySelectorAll('[data-qo-link]').forEach((a) => { a.href = opt.dataset.url; });
      });
    });

    // Rows reflect the cart when the page is shown again from the back/forward cache or another tab changed it.
    const syncFromCart = async () => {
      try {
        cart = await fetch('/cart.js').then((r) => r.json());
        inputs.forEach((i) => {
          if (timers.has(i)) return; // the buyer is mid-edit
          const n = inCart(cart, i);
          i.value = n;
          i.dataset.saved = n;
          setStatus(i, n ? '✓ In cart' : '');
        });
        updateCartCount(cart.item_count);
        update();
      } catch (e) { /* keep what the page rendered */ }
    };
    window.addEventListener('pageshow', (e) => { if (e.persisted) syncFromCart(); });

    // Finish any pending saves (before leaving for the cart, or before the PDF reads the cart).
    qo.flush = () => save(...timers.keys());
    qo.querySelector('[data-qo-checkout]')?.addEventListener('click', async (e) => {
      if (!timers.size) return;
      e.preventDefault();
      const { href } = e.currentTarget;
      await qo.flush();
      window.location.href = href;
    });

    // Text filter, optionally narrowed to the variants this buyer has ordered before.
    const filterInput = qo.querySelector('[data-qo-filter]');
    let orderedBefore = null; // Set of variant ids while "ordered before" is ticked
    // "Show only items in stock" hides sold-out rows (unless they're already in the cart); remembered per browser
    const inStockToggle = qo.querySelector('[data-qo-instock]');
    try { if (inStockToggle) inStockToggle.checked = localStorage.getItem('qh-qo-instock') === '1'; } catch (e) {}
    // "Show only retail packed" keeps listings tagged retail-packed (data-retail); remembered the same way
    const retailToggle = qo.querySelector('[data-qo-retail]');
    try { if (retailToggle) retailToggle.checked = localStorage.getItem('qh-qo-retail') === '1'; } catch (e) {}
    const applyFilter = () => {
      const terms = (filterInput?.value || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
      const inStockOnly = !!inStockToggle?.checked;
      const retailOnly = !!retailToggle?.checked;
      let any = false;
      qo.querySelectorAll('[data-qo-group]').forEach((g) => {
        let shown = 0;
        g.querySelectorAll('[data-qo-row]').forEach((r) => {
          const hit = terms.every((t) => r.dataset.search.includes(t))
            && (!orderedBefore || (r.dataset.variants || '').split(' ').some((v) => orderedBefore.has(v)))
            && (!inStockOnly || !r.classList.contains('is-soldout') || r.classList.contains('is-picked'))
            && (!retailOnly || 'retail' in r.dataset);
          r.hidden = !hit;
          if (hit) shown += 1;
          // typing a variant SKU or color ("pl024", "pink") switches the row's dropdown to that variant
          const select = r.querySelector('[data-qo-select]');
          // retail filter on: a listing with retail and loose packings shows a retail one
          if (hit && select && retailOnly && !('retail' in select.selectedOptions[0].dataset)) {
            const retail = [...select.options].find((o) => 'retail' in o.dataset);
            if (retail) { select.value = retail.value; select.dispatchEvent(new Event('change')); }
          }
          if (hit && select && terms.length) {
            const match = [...select.options].find((o) => terms.every((t) => `${o.dataset.sku} ${o.dataset.label}`.toLowerCase().includes(t)));
            if (match && !match.selected) { select.value = match.value; select.dispatchEvent(new Event('change')); }
          }
        });
        g.hidden = shown === 0;
        if (shown) any = true;
      });
      qo.querySelector('[data-qo-empty]').hidden = any;
    };
    filterInput?.addEventListener('input', applyFilter);
    inStockToggle?.addEventListener('change', () => {
      try { localStorage.setItem('qh-qo-instock', inStockToggle.checked ? '1' : '0'); } catch (e) {}
      applyFilter();
    });
    retailToggle?.addEventListener('change', () => {
      try { localStorage.setItem('qh-qo-retail', retailToggle.checked ? '1' : '0'); } catch (e) {}
      applyFilter();
    });
    if (inStockToggle?.checked || retailToggle?.checked) applyFilter();

    // Column headers stick below the site header + the sticky filter bar while a section scrolls by.
    const tools = qo.querySelector('.quick-order__tools');
    // CSS adds --header-h (kept current by the header code) to the filter bar's height set here
    const setStickyTop = () => {
      qo.style.setProperty('--qo-tools-h', `${tools && getComputedStyle(tools).position === 'sticky' ? tools.offsetHeight : 0}px`);
    };
    setStickyTop();
    window.addEventListener('resize', setStickyTop);
    if (window.ResizeObserver && tools) new ResizeObserver(setStickyTop).observe(tools);

    /* Logged-in buyers: past orders (data-qo-orders, newest first, as [[variantId, cases], ...] per order)
       give "Reorder last order", the "ordered before" filter and a "Last ordered" hint on each row. */
    qo.initHistory = (orders) => {
      const historyBox = qo.querySelector('[data-qo-history]');
      if (!historyBox || !orders.length) return;
      // one panel per variant (rows hold one listing with all its variants)
      const rows = new Map([...qo.querySelectorAll('.quick-order__variant[data-qo-variant]')].map((p) => [p.dataset.qoVariant, p]));
      const inputOf = (v) => qo.querySelector(`[data-qo-qty][data-variant="${v}"]`);
      const skuOf = (panel) => panel.dataset.sku;
      const casesByVariant = (items) => items.reduce((m, [v, q]) => m.set(String(v), (m.get(String(v)) || 0) + q), new Map());
      const plural = (n) => `${n} ${n === 1 ? 'case' : 'cases'}`;

      // the most recent order each variant was in
      const lastSeen = new Map();
      orders.forEach((o) => casesByVariant(o.items).forEach((q, v) => { if (!lastSeen.has(v)) lastSeen.set(v, { q, date: o.date }); }));
      lastSeen.forEach(({ q, date }, v) => {
        // shown under the case size of that variant's panel (visible when the variant is chosen)
        const details = qo.querySelector(`.quick-order__case-text[data-qo-variant="${v}"]`);
        if (!details) return;
        const hint = document.createElement('small');
        hint.className = 'quick-order__last';
        hint.textContent = `Last ordered: ${plural(q)} · ${date}`;
        details.appendChild(hint);
      });
      const known = [...lastSeen.keys()].filter((v) => rows.has(v));

      const last = orders[0];
      const lastItems = casesByVariant(last.items);
      const lastCases = [...lastItems.values()].reduce((a, b) => a + b, 0);
      historyBox.innerHTML = `
        <div class="quick-order__history-text">
          <strong>Your last order</strong>
          <span data-h-summary></span>
        </div>
        <div class="quick-order__history-actions">
          <button type="button" class="button" data-qo-reorder>Reorder last order</button>
          <label class="quick-order__only"><input type="checkbox" data-qo-previous> <span data-h-only></span></label>
        </div>
        <p class="quick-order__history-note" data-qo-history-note hidden></p>`;
      historyBox.querySelector('[data-h-summary]').textContent = `${last.name} · ${last.date} · ${plural(lastCases)}`;
      historyBox.querySelector('[data-h-only]').textContent = `Show only hangers I've ordered before (${known.length})`;
      historyBox.hidden = false;

      historyBox.querySelector('[data-qo-previous]').addEventListener('change', (e) => {
        orderedBefore = e.target.checked ? new Set(known) : null;
        applyFilter();
      });

      const note = historyBox.querySelector('[data-qo-history-note]');
      const reorderBtn = historyBox.querySelector('[data-qo-reorder]');
      reorderBtn.addEventListener('click', async () => {
        reorderBtn.classList.add('is-loading');
        const changed = [];
        const unavailable = [];
        const partial = [];
        let gone = 0;
        lastItems.forEach((q, v) => {
          const row = rows.get(v);
          const input = row && inputOf(v);
          if (!row) { gone += 1; return; }
          if (!input) { unavailable.push(skuOf(row)); return; }
          if (clamp(input) >= q) return; // already in the cart at least that many
          input.value = q;
          const got = clamp(input);
          if (got < q) partial.push(`${skuOf(row)} (${got} of ${q})`);
          if (String(got) !== input.dataset.saved) {
            changed.push(input);
            setStatus(input, 'Saving…');
          }
        });
        update();
        await save(...changed);
        const lines = [changed.length
          ? `Added ${changed.length} ${changed.length === 1 ? 'item' : 'items'} from order ${last.name} to your cart.`
          : `Everything from order ${last.name} that's in stock is already in your cart.`];
        if (partial.length) lines.push(`Limited stock: ${partial.join(', ')}.`);
        if (unavailable.length) lines.push(`Sold out right now: ${unavailable.join(', ')}.`);
        if (gone) lines.push(`${gone} ${gone === 1 ? 'item is' : 'items are'} no longer sold.`);
        note.textContent = lines.join(' ');
        note.hidden = false;
        reorderBtn.classList.remove('is-loading');
      });
    };
    const ordersData = document.querySelector('[data-qo-orders]');
    if (ordersData) {
      try { qo.initHistory(JSON.parse(ordersData.textContent)); } catch (e) { /* page works without the history */ }
    }

    update();
  }

  /* ---------- Collection: grouped color filter ----------
     A group checkbox sends all of its values (hidden inputs) only while it's ticked. */
  const syncGroup = (box) => {
    box.closest('label').querySelectorAll('[data-group-member]').forEach((i) => { i.disabled = !box.checked; });
  };
  document.querySelectorAll('[data-filter-group]').forEach((box) => {
    syncGroup(box);
    box.addEventListener('change', () => syncGroup(box));
  });
  // one chip per color group; its link removes every value of the group
  const chipGroups = {};
  document.querySelectorAll('[data-group-chip]').forEach((li) => {
    (chipGroups[li.dataset.groupChip] = chipGroups[li.dataset.groupChip] || []).push(li);
  });
  Object.values(chipGroups).forEach((items) => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams();
    url.searchParams.forEach((v, k) => {
      if (!items.some((li) => li.dataset.param === k && li.dataset.value === v)) params.append(k, v);
    });
    url.search = params.toString();
    items[0].querySelector('a').href = url.pathname + url.search;
    items.slice(1).forEach((li) => li.remove());
  });

  /* ---------- Collection: auto-submit sort ---------- */
  document.querySelectorAll('[data-autosubmit]').forEach((el) => {
    el.addEventListener('change', () => el.form.submit());
  });

  /* ---------- Product cards: color swatches ----------
     Hover/focus a swatch = show that color's photo and point the card at it.
     Clicking a swatch opens the product with that color selected. */
  const previewSwatch = (swatch) => {
    const card = swatch.closest('[data-card]');
    if (!card) return;
    const img = card.querySelector('.card__image:not(.card__image--hover)');
    if (img && swatch.dataset.swatchImg) {
      img.removeAttribute('srcset');
      img.src = swatch.dataset.swatchImg;
    }
    card.querySelectorAll('[data-card-link]').forEach((a) => { a.href = swatch.dataset.swatchUrl; });
    card.querySelectorAll('.card__swatch').forEach((s) => s.classList.toggle('is-active', s === swatch));
  };
  document.addEventListener('mouseover', (e) => {
    const swatch = e.target.closest('.card__swatch');
    if (swatch) previewSwatch(swatch);
  });
  document.addEventListener('focusin', (e) => {
    const swatch = e.target.closest('.card__swatch');
    if (swatch) previewSwatch(swatch);
  });

  /* ---------- Header: instant search ----------
     Shows matching products under the search bar while typing (SKU, code or words).
     The product list comes from /collections/all?view=search-index, loaded once per visit. */
  let searchIndex = null;
  const loadIndex = () => {
    if (!searchIndex) {
      searchIndex = (async () => {
        const items = [];
        let url = '/collections/all?view=search-index';
        while (url) {
          const data = await fetch(url, { credentials: 'same-origin' }).then((r) => r.json());
          items.push(...data.items);
          url = data.next;
        }
        return items.map((it) => {
          const title = it.t.toLowerCase();
          return Object.assign(it, {
            title,
            words: title.split(/[^a-z0-9.]+/).filter(Boolean),
            skus: it.s.toLowerCase().split(/\s+/).filter(Boolean),
            rest: it.k.toLowerCase(),
          });
        });
      })().catch(() => { searchIndex = null; return []; });
    }
    return searchIndex;
  };

  // lower score = better match; null = no match
  const scoreItem = (it, terms) => {
    let total = 0;
    for (const term of terms) {
      let s = null;
      if (it.title.startsWith(term)) s = 0;
      else if (it.words.some((w) => w.startsWith(term))) s = 1;
      else if (it.skus.some((k) => k.startsWith(term))) s = 2;
      else if (it.title.includes(term) || it.rest.includes(term)) s = 3;
      else if (it.skus.some((k) => k.includes(term))) s = 4;
      if (s === null) return null;
      total += s;
    }
    return total + (it.a ? 0 : 10);   // sold-out listings go last
  };

  const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  document.querySelectorAll('.header__search').forEach((form) => {
    const input = form.querySelector('input[name="q"]');
    if (!input) return;
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-expanded', 'false');
    const box = document.createElement('div');
    box.className = 'predictive';
    box.id = `${input.id}-results`;
    box.setAttribute('role', 'listbox');
    box.hidden = true;
    input.setAttribute('aria-controls', box.id);
    form.appendChild(box);
    let active = -1;
    let timer;

    const close = () => { box.hidden = true; active = -1; input.setAttribute('aria-expanded', 'false'); };
    const links = () => [...box.querySelectorAll('.predictive__item')];
    const highlight = (i) => {
      const all = links();
      active = (i + all.length) % all.length;
      all.forEach((a, n) => a.classList.toggle('is-active', n === active));
      all[active]?.scrollIntoView({ block: 'nearest' });
    };

    const render = async () => {
      const q = input.value.trim().toLowerCase();
      if (!q) return close();
      const items = await loadIndex();
      if (input.value.trim().toLowerCase() !== q) return;   // typed on while loading
      const terms = q.split(/\s+/);
      const hits = items
        .map((it) => ({ it, s: scoreItem(it, terms) }))
        .filter((h) => h.s !== null)
        .sort((a, b) => a.s - b.s || a.it.title.localeCompare(b.it.title))
        .slice(0, 8);
      const allUrl = `${form.action}?q=${encodeURIComponent(input.value.trim())}`;
      box.innerHTML = (hits.length
        ? hits.map(({ it }) => `
          <a class="predictive__item" role="option" href="${escapeHtml(it.u)}">
            <span class="predictive__img">${it.i ? `<img src="${escapeHtml(it.i)}" alt="" loading="lazy" width="48" height="48">` : ''}</span>
            <span class="predictive__text">
              <span class="predictive__title">${escapeHtml(it.t)}</span>
              <span class="predictive__price">${it.v ? 'From ' : ''}${escapeHtml(it.p)} / case${it.a ? '' : ' · <em>Sold out</em>'}</span>
            </span>
          </a>`).join('')
        : `<p class="predictive__empty">No hangers found for “${escapeHtml(input.value.trim())}”</p>`)
        + `<a class="predictive__all" href="${escapeHtml(allUrl)}">See all results for “${escapeHtml(input.value.trim())}” →</a>`;
      box.hidden = false;
      active = -1;
      input.setAttribute('aria-expanded', 'true');
    };

    input.addEventListener('focus', loadIndex, { once: true });
    input.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(render, 120); });
    input.addEventListener('keydown', (e) => {
      if (box.hidden) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); highlight(active + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); highlight(active - 1); }
      else if (e.key === 'Escape') close();
      else if (e.key === 'Enter' && active >= 0) { e.preventDefault(); links()[active].click(); }
    });
    document.addEventListener('click', (e) => { if (!form.contains(e.target)) close(); });
  });
})();

/* Back in stock: "Notify me" buttons (product page, Quick Order) open one dialog (snippets/back-in-stock).
   The request goes through the app proxy to the Quality Hangers Quotes app (quote-worker), which emails
   the shopper once the item is back. */
(function () {
  const dialog = document.querySelector('[data-bis-dialog]');
  if (!dialog) return;
  const form = dialog.querySelector('[data-bis-form]');
  const done = dialog.querySelector('[data-bis-done]');
  const email = dialog.querySelector('[data-bis-email]');
  const error = dialog.querySelector('[data-bis-error]');
  const submit = dialog.querySelector('[data-bis-submit]');
  let current = null;

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-bis-open]');
    if (!btn) return;
    e.preventDefault();
    current = { variantId: btn.dataset.variant, handle: btn.dataset.handle, sku: btn.dataset.sku || '' };
    dialog.querySelectorAll('[data-bis-sku]').forEach((el) => { el.textContent = current.sku || 'this item'; });
    form.hidden = false; done.hidden = true; error.hidden = true; submit.disabled = false;
    if (typeof dialog.showModal === 'function') dialog.showModal(); else dialog.setAttribute('open', '');
    setTimeout(() => email.focus(), 50);
  });
  dialog.querySelectorAll('[data-bis-close]').forEach((b) => b.addEventListener('click', () => dialog.close()));
  dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });   // click outside

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (!current) return;
    error.hidden = true;
    if (!email.checkValidity()) { error.textContent = 'Please enter a valid email address.'; error.hidden = false; return; }
    submit.disabled = true;
    try {
      const res = await fetch('/apps/qh-quote/notify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...current, email: email.value.trim() })
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) throw new Error(body.error || 'Something went wrong. Please try again.');
      dialog.querySelector('[data-bis-done-text]').textContent = body.available
        ? `Good news: ${current.sku || 'this item'} is in stock right now. Refresh the page to order it.`
        : `We'll email ${email.value.trim()} as soon as ${current.sku || 'this item'} is back in stock.`;
      form.hidden = true; done.hidden = false;
      try { localStorage.setItem('qh-bis-email', email.value.trim()); } catch (err) {}
    } catch (err) {
      error.textContent = err.message; error.hidden = false; submit.disabled = false;
    }
  });
  try { if (!email.value) email.value = localStorage.getItem('qh-bis-email') || ''; } catch (err) {}
})();